import { useState, useEffect, useMemo } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Building2, LayoutDashboard, PlusCircle, UserSearch, Menu, PanelLeftOpen, PanelLeftClose } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useCompanyAccess } from "@/hooks/useCompanyAccess";
import { isStartPlan, hasActiveCredits } from "@/lib/planUtils";

const allMenuItems = [
  { title: "Dashboard", url: "/empresa/dashboard", icon: LayoutDashboard },
  { title: "Banco de Talentos", url: "/empresa/banco-talentos", icon: UserSearch, requiresPaidPlan: true },
  { title: "Criar Vaga", url: "/empresa/criar-vaga", icon: PlusCircle },
  { title: "Minha Conta", url: "/empresa/minha-conta", icon: Building2 },
];

export function EmpresaSidebar() {
  const location = useLocation();
  const { companyProfile } = useCompanyAccess(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState(false);

  // Filter menu items based on company plan and credits
  const menuItems = useMemo(() => {
    const isStart = isStartPlan(companyProfile?.plan);
    const hasCredits = hasActiveCredits(
      companyProfile?.credits,
      companyProfile?.credits_expires_at
    );
    
    return allMenuItems.filter(item => {
      // Show Banco de Talentos for paid plans OR users with active credits
      if (item.requiresPaidPlan && isStart && !hasCredits) {
        return false;
      }
      return true;
    });
  }, [companyProfile?.plan, companyProfile?.credits, companyProfile?.credits_expires_at]);

  // Auto-collapse on smaller screens (between md and lg)
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      const shouldCollapse = width < 1024 && width >= 768;
      setIsSmallScreen(shouldCollapse);
      if (shouldCollapse) {
        setIsCollapsed(true);
      } else if (width >= 1024) {
        setIsCollapsed(false);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname]);

  const SidebarContent = ({ mobile = false }: { mobile?: boolean }) => (
    <nav className={cn("p-4 space-y-2", mobile && "pt-2")}>
      {menuItems.map((item) => {
        const isActive = location.pathname === item.url;
        const linkContent = (
          <NavLink
            key={item.url}
            to={item.url}
            className={cn(
              "flex items-center gap-3 px-4 py-3 rounded-lg transition-colors",
              isActive
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:bg-muted hover:text-foreground",
              !mobile && isCollapsed && "justify-center px-3"
            )}
          >
            <item.icon className="h-5 w-5 shrink-0" />
            {(mobile || !isCollapsed) && (
              <span className="font-medium">{item.title}</span>
            )}
          </NavLink>
        );

        // Show tooltip when collapsed (desktop only)
        if (!mobile && isCollapsed) {
          return (
            <Tooltip key={item.url} delayDuration={0}>
              <TooltipTrigger asChild>
                {linkContent}
              </TooltipTrigger>
              <TooltipContent side="right" className="font-medium">
                {item.title}
              </TooltipContent>
            </Tooltip>
          );
        }

        return linkContent;
      })}
    </nav>
  );

  return (
    <TooltipProvider>
      {/* Mobile trigger button */}
      <Sheet open={isMobileOpen} onOpenChange={setIsMobileOpen}>
        <SheetTrigger asChild>
          <Button
            variant="outline"
            size="icon"
            className="fixed left-4 top-20 z-40 md:hidden"
          >
            <Menu className="h-5 w-5" />
          </Button>
        </SheetTrigger>
        <SheetContent side="left" className="w-64 p-0">
          <div className="flex items-center justify-between p-4 border-b">
            <span className="font-semibold text-foreground">Menu</span>
          </div>
          <SidebarContent mobile />
        </SheetContent>
      </Sheet>

      {/* Desktop sidebar */}
      <aside
        className={cn(
          "shrink-0 border-r bg-card hidden md:flex flex-col transition-all duration-300",
          isCollapsed ? "w-[72px]" : "w-64"
        )}
      >
        {/* Toggle button - shows on tablet screens (between md and lg) */}
        {isSmallScreen && (
          <div className={cn(
            "p-2 border-b flex",
            isCollapsed ? "justify-center" : "justify-end"
          )}>
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setIsCollapsed(!isCollapsed)}
                  className="h-8 w-8 text-muted-foreground hover:text-foreground"
                >
                  {isCollapsed ? (
                    <PanelLeftOpen className="h-4 w-4" />
                  ) : (
                    <PanelLeftClose className="h-4 w-4" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">
                {isCollapsed ? "Expandir menu" : "Minimizar menu"}
              </TooltipContent>
            </Tooltip>
          </div>
        )}

        <SidebarContent />
      </aside>
    </TooltipProvider>
  );
}
